import React, { useState, useEffect } from "react";
import { Box, Button, Grid, TextField, Typography, Paper, List, ListItem, ListItemText, Modal, IconButton } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAPI } from "app/hooks/useApi";
import moment from "moment";

const initialForm = {
  data: "",
  descricao: "",
  protocolo: "",
  detalhes: "",
};

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const ProcessosOcorrencias = ({ occurrences = [], onAddOccurrence, processId, onSyncOccurrences, onEditOccurrence }) => {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const { makeRequest } = useAPI();
  const [loading, setLoading] = useState(false);
  const [localOccurrences, setLocalOccurrences] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0); // 0: nenhum, 1: primeira, 2: segunda
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    setLocalOccurrences(occurrences);
  }, [occurrences]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.data || !form.descricao || !form.protocolo || !form.detalhes) {
      setError("Preencha todos os campos.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      if (editingId) {
        if (onEditOccurrence) {
          await onEditOccurrence(editingId, form);
        }
      } else {
        const payload = {
          data: form.data,
          descricao: form.descricao,
          protocolo: form.protocolo,
          detalhes: form.detalhes,
        };
        const url = `processos-revistas-rpi/processos/${processId}/ocorrencias`;
        const response = await makeRequest(url, 'POST', payload);
        if (response.status === 201 || response.status === 200) {
          if (onSyncOccurrences) {
            await onSyncOccurrences(); 
          } else {
            onAddOccurrence({ ...form, id: response.data?.id || Date.now() });
          }
        } else {
          setError("Erro ao salvar ocorrência no backend.");
          setLoading(false);
          return;
        }
      }
      setForm(initialForm);
      setEditingId(null);
      setOpen(false);
    } catch (err) {
      setError("Erro ao salvar ocorrência no backend.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (occurrenceId) => {
    setDeleteTarget(occurrenceId);
    setDeleteStep(1);
  };

  const handleDeleteConfirm = async () => {
    if (deleteStep === 1) {
      setDeleteStep(2);
      return;
    }
    if (deleteStep === 2 && deleteTarget) {
      setLoading(true);
      try {
        await makeRequest(`processos-revistas-rpi/processos/${processId}/ocorrencias/${deleteTarget}`, 'DELETE');
        if (onSyncOccurrences) {
          await onSyncOccurrences();
        } else {
          setLocalOccurrences((prev) => prev.filter(o => o.id !== deleteTarget));
        }
      } catch (error) {

      } finally {
        setLoading(false);
        setDeleteStep(0);
        setDeleteTarget(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteStep(0);
    setDeleteTarget(null);
  };

  const openEditModal = (occ) => {
    setEditingId(occ.id);
    setForm({
      data: occ.data || '',
      descricao: occ.descricao || '',
      protocolo: occ.protocolo || '',
      detalhes: occ.detalhes || '',
    });
    setOpen(true);
  };

  const openAddModal = () => {
    setEditingId(null);
    setForm(initialForm);
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
    setEditingId(null);
    setForm(initialForm);
    setError("");
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Ocorrências</Typography>
        <Button variant="contained" color="primary" onClick={openAddModal}>
          Nova Ocorrência
        </Button>
      </Box>
      <Modal open={open} onClose={handleCloseModal}>
        <Box sx={{ ...style, width: 800, maxWidth: '98vw' }} component="form" onSubmit={handleSubmit}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{editingId ? 'Editar Ocorrência' : 'Cadastrar Ocorrência'}</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Data"
                name="data"
                type="date"
                value={form.data}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Protocolo"
                name="protocolo"
                value={form.protocolo}
                onChange={handleChange}
                fullWidth
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Descrição"
                name="descricao"
                value={form.descricao}
                onChange={handleChange}
                fullWidth
                required
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Detalhes"
                name="detalhes"
                value={form.detalhes}
                onChange={handleChange}
                fullWidth
                required
                multiline
                minRows={3}
                maxRows={8}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ mt: 2 }}>
                Salvar
              </Button>
              {error && (
                <Typography color="error" variant="body2" mt={1}>
                  {error}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <List>
        {localOccurrences.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Nenhuma ocorrência cadastrada.
          </Typography>
        )}
        {localOccurrences.map((occ) => (
          <ListItem key={occ.id} alignItems="flex-start" divider
            secondaryAction={
              <>
                <Button color="primary" onClick={() => openEditModal(occ)} size="small">Editar</Button>
                <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteClick(occ.id)}>
                  <DeleteIcon />
                </IconButton>
              </>
            }
          >
            <ListItemText
              primary={`Data: ${occ.data ? moment(occ.data).format('DD/MM/YYYY') : ''} | Protocolo: ${occ.protocolo}`}
              secondary={
                <>
                  <Typography component="span" variant="subtitle2">
                    {occ.descricao}
                  </Typography>
                  <br />
                  <Typography component="span" variant="body2" color="text.secondary">
                    {occ.detalhes}
                  </Typography>
                </>
              }
            />
          </ListItem>
        ))}
      </List>
      <Modal open={deleteStep > 0} onClose={handleDeleteCancel}>
        <Box sx={{ ...style, width: 360, textAlign: 'center' }}>
          <Typography variant="h6" mb={2}>
            {deleteStep === 1 ? 'Confirmar exclusão' : 'Atenção!'}
          </Typography>
          <Typography mb={3}>
            {deleteStep === 1
              ? 'Tem certeza que deseja deletar esta ocorrência?'
              : 'Esta ação é irreversível. Deseja realmente excluir?'}
          </Typography>
          <Box display="flex" justifyContent="center" gap={2}>
            <Button onClick={handleDeleteCancel} variant="outlined" color="primary" disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleDeleteConfirm} variant="contained" color={deleteStep === 2 ? 'error' : 'primary'} disabled={loading}>
              {deleteStep === 1 ? 'Sim, continuar' : 'Excluir'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Paper>
  );
};

export default ProcessosOcorrencias;
